import { useEffect, useMemo, useState } from 'react';
import type { Todo, TodoNavigationSummary } from '../types/data';
import { todoLocalRepo } from '../utils/localDb/todoLocalRepo';
import { todoNavigationSummaryRepo } from '../utils/repos/todoNavigationSummaryRepo';
import { addTokyoDays, formatTokyoDateParts, getTokyoDateParts } from '../utils/dateUtils';

const BACKEND = (process.env.REACT_APP_BACKEND || 'local').toLowerCase();

const getCalendarRange = (currentMonth?: Date): { from: string; to: string } => {
  const base = currentMonth ?? new Date();
  const { year, month } = getTokyoDateParts(base);
  const firstDay = new Date(Date.UTC(year, month - 1, 1));
  const firstDayString = formatTokyoDateParts({ year, month, day: 1 });
  const start = addTokyoDays(firstDayString, -firstDay.getUTCDay()) ?? firstDayString;
  const end = addTokyoDays(start, 41) ?? start;

  return {
    from: start,
    to: end,
  };
};

const getTodoSummaryRefreshKey = (todos: Todo[]): string => {
  return todos
    .map(todo => [
      todo.id,
      todo.modifiedAt,
      todo.done ? '1' : '0',
      todo.canceled ? '1' : '0',
      todo.deleted ? '1' : '0',
      todo.startAt,
      todo.habitId ?? '',
      (todo.todoTagIds ?? []).join(','),
    ].join(':'))
    .join('|');
};

export const useTodoNavigationSummary = (currentMonth: Date | undefined, todos: Todo[], refreshSignal = 0) => {
  const [summary, setSummary] = useState<TodoNavigationSummary | null>(null);
  const [error, setError] = useState<unknown>(null);

  const range = useMemo(() => getCalendarRange(currentMonth), [currentMonth]);
  const refreshKey = useMemo(() => getTodoSummaryRefreshKey(todos), [todos]);

  useEffect(() => {
    if (BACKEND !== 'supabase') {
      setSummary(null);
      setError(null);
      return;
    }

    let alive = true;
    (async () => {
      try {
        const calendar = await todoLocalRepo.listCalendarBadgeItems(range.from, range.to);
        if (!alive) return;
        setSummary(prev => ({
          generated_at: new Date().toISOString(),
          calendar_from: range.from,
          calendar_to: range.to,
          timezone: 'Asia/Tokyo',
          calendar,
          tag_counts: prev?.tag_counts ?? [],
          habit_counts: prev?.habit_counts ?? [],
        }));
      } catch (e) {
        if (!alive) return;
        console.error('Failed to load Todo calendar badges from IndexedDB:', e);
      }
    })();

    const timer = window.setTimeout(() => {
      (async () => {
        try {
          const next = await todoNavigationSummaryRepo.getTodoNavigationSummary({
            calendarFrom: range.from,
            calendarTo: range.to,
            timezone: 'Asia/Tokyo',
          });
          if (!alive) return;
          setSummary(next);
          setError(null);
        } catch (e) {
          if (!alive) return;
          console.error('Failed to load Todo navigation summary:', e);
          setError(e);
        }
      })();
    }, 500);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [range.from, range.to, refreshKey, refreshSignal]);

  return { summary, error };
};

export default useTodoNavigationSummary;
